import { useCallback, useEffect, useState } from "react";
import { adminApi, API } from "../../lib/api";
import { toast } from "sonner";
import { X, Upload, RotateCcw, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { CATEGORIES, TILE_META_EXTRAS } from "../../constants/data";
import { refreshCategoryImages } from "../../hooks/useCategoryImages";

const TILE_SLUGS = ["her", "him", "kids", "babies", "baskets", "diwali", "founder", "about-side", "hero-bg"];

/**
 * AdminTiles — homepage / shop tile image manager (self-contained).
 * Baskets shows 2x2 (4 images) with drag-to-reorder; others show a single image.
 */
export default function AdminTiles() {
  const [tileImages, setTileImages] = useState({});
  const [tileBusy, setTileBusy] = useState({});
  const [dragTileImg, setDragTileImg] = useState(null);

  const reload = useCallback(
    () => adminApi().get("/category-images").then((r) => setTileImages(r.data || {})).catch(() => {}),
    [],
  );
  useEffect(() => { reload(); }, [reload]);

  const uploadTileImage = async (slug, idx, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setTileBusy((b) => ({ ...b, [`${slug}-${idx}`]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await adminApi().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${API}${up.data.url.replace(/^\/api/, "")}`;
      const current = Array.isArray(tileImages[slug]) ? [...tileImages[slug]] : [];
      current[idx] = fullUrl;
      const trimmed = current.filter(Boolean).slice(0, 4);
      await adminApi().put(`/admin/category-images/${slug}`, { images: trimmed });
      toast.success("Tile image saved");
      await reload();
      refreshCategoryImages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setTileBusy((b) => ({ ...b, [`${slug}-${idx}`]: false }));
    }
  };

  const removeTileImage = async (slug, idx) => {
    if (!window.confirm("Remove this image?")) return;
    const current = Array.isArray(tileImages[slug]) ? [...tileImages[slug]] : [];
    const updated = current.filter((_, i) => i !== idx);
    setTileBusy((b) => ({ ...b, [`${slug}-${idx}`]: true }));
    try {
      if (updated.length === 0) {
        await adminApi().delete(`/admin/category-images/${slug}`);
      } else {
        await adminApi().put(`/admin/category-images/${slug}`, { images: updated });
      }
      toast.success("Removed");
      await reload();
      refreshCategoryImages();
    } catch {
      toast.error("Failed");
    } finally {
      setTileBusy((b) => ({ ...b, [`${slug}-${idx}`]: false }));
    }
  };

  const resetTile = async (slug) => {
    if (!window.confirm("Reset this tile to the default image?")) return;
    try {
      await adminApi().delete(`/admin/category-images/${slug}`);
      toast.success("Reset to default");
      await reload();
      refreshCategoryImages();
    } catch {
      toast.error("Reset failed");
    }
  };

  const bulkUploadTileImages = async (slug, files) => {
    if (!files || files.length === 0) return;
    const max = slug === "baskets" ? 4 : 1;
    const current = (Array.isArray(tileImages[slug]) ? tileImages[slug] : []).filter(Boolean);
    const remaining = max - current.length;
    if (remaining <= 0) { toast.error(`Tile already has the max ${max} image${max > 1 ? "s" : ""}.`); return; }
    const slice = Array.from(files).slice(0, remaining);
    setTileBusy((b) => ({ ...b, [`${slug}-bulk`]: true }));
    const added = [];
    try {
      for (const f of slice) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`Skipped ${f.name}: over 5MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const up = await adminApi().post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(`${API}${up.data.url.replace(/^\/api/, "")}`);
      }
      if (added.length === 0) return;
      const next = [...current, ...added].slice(0, max);
      await adminApi().put(`/admin/category-images/${slug}`, { images: next });
      toast.success(`${added.length} image${added.length > 1 ? "s" : ""} added`);
      await reload();
      refreshCategoryImages();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setTileBusy((b) => ({ ...b, [`${slug}-bulk`]: false }));
    }
  };

  const reorderTileImage = async (slug, from, to) => {
    const arr = (Array.isArray(tileImages[slug]) ? tileImages[slug] : []).filter(Boolean);
    if (to < 0 || to >= arr.length || from === to) return;
    const next = [...arr];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    try {
      await adminApi().put(`/admin/category-images/${slug}`, { images: next });
      await reload();
      refreshCategoryImages();
    } catch { toast.error("Could not reorder"); }
  };

  return (
    <div className="mt-6" data-testid="admin-tiles-tab">
      <p className="text-sm text-[#5C5555] mb-4">Upload images for each homepage tile. <b>Baskets Collection</b> shows a 2×2 grid (4 images) — drag images to reorder, multi-upload supported. Other tiles show a single image.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {TILE_SLUGS.map((slug) => {
          const meta = CATEGORIES.find((c) => c.slug === slug) || TILE_META_EXTRAS[slug];
          const arr = Array.isArray(tileImages[slug]) ? tileImages[slug] : [];
          const isCustom = arr.length > 0;
          const slotCount = slug === "baskets" ? 4 : 1;
          const filled = arr.filter(Boolean).length;
          const bulkBusy = !!tileBusy[`${slug}-bulk`];
          return (
            <div key={slug} className="bg-white border border-[#E5DBD8] p-4 rounded-lg" data-testid={`tile-card-${slug}`}>
              <div className="flex items-baseline justify-between mb-2">
                <p className="font-serif text-lg text-[#2D2424]">{meta?.name || slug}</p>
                {isCustom && <span className="text-[10px] bg-[#7A1F2B] text-white px-2 py-0.5 rounded-full uppercase tracking-wider">Custom</span>}
              </div>
              <p className="text-[10px] text-[#5C5555] mb-3 uppercase tracking-widest">/{slug} · {slotCount === 4 ? "2×2 grid" : "single image"}</p>

              {slotCount > 1 && (
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2 px-2.5 py-1.5 bg-[#FFF6F0] border border-dashed border-[#7A1F2B]/30 rounded">
                  <p className="text-[11px] text-[#5C5555]"><span className="font-semibold text-[#2D2424]">{filled}/{slotCount}</span></p>
                  <label className={`inline-flex items-center gap-1 px-2.5 py-1 bg-[#7A1F2B] text-white text-[10px] uppercase tracking-wider rounded cursor-pointer hover:bg-[#5C1520] ${filled >= slotCount || bulkBusy ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <Images className="w-3 h-3"/> {bulkBusy ? "…" : "Multi-upload"}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      disabled={filled >= slotCount || bulkBusy}
                      className="hidden"
                      data-testid={`tile-bulk-upload-${slug}`}
                      onChange={(e) => { bulkUploadTileImages(slug, e.target.files); e.target.value = ""; }}
                    />
                  </label>
                </div>
              )}

              <div className={`grid ${slotCount === 4 ? "grid-cols-2" : "grid-cols-1"} gap-2 mb-3`}>
                {Array.from({ length: slotCount }).map((_, idx) => {
                  const url = arr[idx];
                  const busy = !!tileBusy[`${slug}-${idx}`];
                  const fallback = !url ? meta?.image : null;
                  const canMoveLeft = !!url && idx > 0;
                  const canMoveRight = !!url && idx < filled - 1;
                  const isDragging = dragTileImg && dragTileImg.slug === slug && dragTileImg.from === idx;
                  const isDropTarget = dragTileImg && dragTileImg.slug === slug && dragTileImg.over === idx && dragTileImg.from !== idx;
                  return (
                    <div
                      key={idx}
                      className={`relative ${slotCount === 4 ? "aspect-square" : "aspect-[4/3]"} border rounded overflow-hidden group transition-all ${isDropTarget ? "border-[#7A1F2B] border-2 ring-2 ring-[#7A1F2B]/30 scale-[1.03]" : "border-[#E5DBD8]"} ${isDragging ? "opacity-40" : ""} bg-[#FFE7F1]`}
                      draggable={!!url && slotCount > 1 && filled > 1}
                      onDragStart={() => url && slotCount > 1 && setDragTileImg({ slug, from: idx, over: idx })}
                      onDragEnter={(e) => { if (dragTileImg && dragTileImg.slug === slug && url) { e.preventDefault(); setDragTileImg((d) => ({ ...d, over: idx })); } }}
                      onDragOver={(e) => { if (dragTileImg && dragTileImg.slug === slug && url) e.preventDefault(); }}
                      onDrop={(e) => {
                        if (dragTileImg && dragTileImg.slug === slug && url) {
                          e.preventDefault();
                          const from = dragTileImg.from;
                          setDragTileImg(null);
                          if (from !== idx) reorderTileImage(slug, from, idx);
                        }
                      }}
                      onDragEnd={() => setDragTileImg(null)}
                    >
                      {url ? (
                        <>
                          <img src={url} alt="" className="w-full h-full object-cover pointer-events-none"/>
                          {slotCount > 1 && (
                            <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-white/95 border border-[#2D2424] text-[10px] font-doodle text-[#7A1F2B] leading-none">#{idx + 1}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeTileImage(slug, idx)}
                            data-testid={`tile-img-remove-${slug}-${idx}`}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white border border-[#2D2424] flex items-center justify-center hover:bg-[#FFE7F1]"
                            aria-label="remove"
                          ><X className="w-3.5 h-3.5"/></button>
                          {slotCount > 1 && filled > 1 && (
                            <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                              <button
                                type="button"
                                disabled={!canMoveLeft}
                                onClick={() => reorderTileImage(slug, idx, idx - 1)}
                                data-testid={`tile-img-move-left-${slug}-${idx}`}
                                className="w-6 h-6 rounded-full bg-white border border-[#2D2424] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#FFE7F1]"
                                aria-label="move left"
                              ><ChevronLeft className="w-3.5 h-3.5"/></button>
                              <button
                                type="button"
                                disabled={!canMoveRight}
                                onClick={() => reorderTileImage(slug, idx, idx + 1)}
                                data-testid={`tile-img-move-right-${slug}-${idx}`}
                                className="w-6 h-6 rounded-full bg-white border border-[#2D2424] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#FFE7F1]"
                                aria-label="move right"
                              ><ChevronRight className="w-3.5 h-3.5"/></button>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {fallback && (
                            <img src={fallback} alt="" className="w-full h-full object-cover opacity-40"/>
                          )}
                          <label className={`absolute inset-0 flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-[#FFD6E7]/70 text-[#7A1F2B] text-xs font-hand ${busy ? "opacity-60" : ""}`}>
                            <Upload className="w-4 h-4"/>
                            <span>{busy ? "Uploading…" : (slotCount === 4 ? `Image ${idx + 1}` : "Upload image")}</span>
                            <input
                              type="file"
                              accept="image/*"
                              disabled={busy}
                              className="hidden"
                              data-testid={`tile-upload-${slug}-${idx}`}
                              onChange={(e) => { uploadTileImage(slug, idx, e.target.files?.[0]); e.target.value = ""; }}
                            />
                          </label>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              {isCustom && (
                <button
                  type="button"
                  onClick={() => resetTile(slug)}
                  data-testid={`tile-reset-${slug}`}
                  className="w-full px-3 py-2 border border-[#E5DBD8] text-xs uppercase tracking-wider rounded hover:bg-[#F2E8E4] inline-flex items-center justify-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5"/> Reset to default
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
